import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './VerifyClaim.css';

const VerifyClaim = () => {
    const { email: urlEmail, month: urlMonth, year: urlYear } = useParams();
    const navigate = useNavigate();

    const [email, setEmail] = useState(urlEmail || '');
    const [month, setMonth] = useState(urlMonth || '');
    const [year, setYear] = useState(urlYear || new Date().getFullYear().toString());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => (currentYear - 2 + i).toString());

    useEffect(() => {
        if (urlEmail && urlMonth && urlYear) {
            handleVerify(urlEmail, urlMonth, urlYear);
        }
    }, [urlEmail, urlMonth, urlYear]);

    const handleVerify = async (eOrEmail, manualMonth, manualYear) => {
        if (typeof eOrEmail === 'object') {
            eOrEmail.preventDefault();
        }

        const targetEmail = typeof eOrEmail === 'string' ? eOrEmail : email;
        const targetMonth = manualMonth || month;
        const targetYear = manualYear || year;

        if (!targetEmail || !targetMonth || !targetYear) {
            setError('Please provide Email, Month, and Year');
            return;
        }

        setLoading(true);
        setError('');
        setResult(null);

        try {
            const response = await api.get(`/public/verify-claim/${targetEmail}/${targetMonth}/${targetYear}`);
            if (response.data.success) {
                setResult(response.data);
                // Update URL for easy sharing
                window.history.replaceState(null, '', `/verifyclaim/${targetEmail}/${targetMonth}/${targetYear}`);
            } else {
                setError(response.data.message || 'Failed to verify claim');
            }
        } catch (err) {
            console.error('Verification error:', err);
            setError(err.response?.data?.message || 'Error occurred while fetching claim status');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'approved':
            case 'paid':
            case 'dean_approved':
            case 'allocated':
                return '#10b981';
            case 'rejected':
                return '#ef4444';
            case 'pending':
            case 'submitted':
                return '#f59e0b';
            default:
                return '#6b7280';
        }
    };

    const formatStatus = (status) => {
        if (!status) return 'Pending';
        return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    return (
        <div className="verify-claim-page">
            <header className="landing-header">
                <div className="header-container">
                    <div className="logo-section" onClick={() => navigate('/home')} style={{ cursor: 'pointer' }}>
                        <img src="/lareg_logo.png" alt="TCE Logo" className="tce-logo" />
                    </div>
                    <nav className="header-nav">
                        <a href="/home" className="nav-link">Home</a>
                        <button onClick={() => navigate('/login')} className="login-btn">Login</button>
                    </nav>
                </div>
            </header>

            <div className="verify-content">
                <div className="verify-card">
                    <h2 className="verify-title">Check Claim Status</h2>
                    <p className="verify-subtitle">Enter scholar details to track claim progress</p>

                    <form onSubmit={handleVerify} className="verify-form">
                        <div className="form-group">
                            <label>Scholar Email ID</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="e.g. scholar@tce.edu"
                                required
                            />
                        </div>
                        <div className="form-row" style={{ display: 'flex', gap: '1rem' }}>
                            <div className="form-group" style={{ flex: 2 }}>
                                <label>Claim Month</label>
                                <select
                                    value={month}
                                    onChange={(e) => setMonth(e.target.value)}
                                    required
                                >
                                    <option value="">Select Month</option>
                                    {months.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                            </div>
                            <div className="form-group" style={{ flex: 1 }}>
                                <label>Year</label>
                                <select
                                    value={year}
                                    onChange={(e) => setYear(e.target.value)}
                                    required
                                >
                                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                        </div>
                        <button type="submit" className="verify-btn" disabled={loading}>
                            {loading ? 'Searching...' : 'Check Status'}
                        </button>
                    </form>

                    {error && <div className="verify-error">{error}</div>}

                    {result && (
                        <div className="verify-result">
                            <div className="result-section">
                                <h3>Scholar Details</h3>
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label>Name</label>
                                        <span>{result.scholar.name}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Roll No</label>
                                        <span>{result.scholar.rollno}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Department</label>
                                        <span>{result.scholar.dept}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Supervisor</label>
                                        <span>{result.scholar.Supervisor}</span>
                                    </div>
                                </div>
                            </div>

                            <hr className="divider" />

                            <div className="result-section">
                                <h3>Claim Information ({month})</h3>
                                {result.claim ? (
                                    <>
                                        <div className="status-badge" style={{ backgroundColor: getStatusColor(result.claim.status) }}>
                                            {formatStatus(result.claim.status)}
                                        </div>
                                        <div className="detail-grid">
                                            <div className="detail-item">
                                                <label>Claim Number</label>
                                                <span>{result.claim.claimNumber}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Amount</label>
                                                <span>₹{result.claim.claim_amount?.toLocaleString()}</span>
                                            </div>
                                            <div className="detail-item">
                                                <label>Submitted On</label>
                                                <span>{result.claim.submittedAt ? new Date(result.claim.submittedAt._seconds * 1000).toLocaleDateString() : '-'}</span>
                                            </div>
                                        </div>

                                        <div className="approval-timeline">
                                            <h4>Approval Progress</h4>
                                            <div className="timeline-steps">
                                                <div className={`step ${result.claim.isSupervisorApproved === 'approved' ? 'completed' : result.claim.isSupervisorApproved === 'rejected' ? 'failed' : 'pending'}`}>
                                                    <span className="dot"></span>
                                                    <span className="label">Supervisor</span>
                                                </div>
                                                <div className={`step ${result.claim.isDlcApproved === 'approved' ? 'completed' : 'pending'}`}>
                                                    <span className="dot"></span>
                                                    <span className="label">DLC</span>
                                                </div>
                                                <div className={`step ${result.claim.isHodApproved === 'approved' ? 'completed' : result.claim.isHodApproved === 'rejected' ? 'failed' : 'pending'}`}>
                                                    <span className="dot"></span>
                                                    <span className="label">HOD</span>
                                                </div>
                                                <div className={`step ${result.claim.isadminApproved === 'approved' ? 'completed' : 'pending'}`}>
                                                    <span className="dot"></span>
                                                    <span className="label">Admin/Dean</span>
                                                </div>
                                                <div className={`step ${result.claim.isAllocated === 'allocated' ? 'completed' : 'pending'}`}>
                                                    <span className="dot"></span>
                                                    <span className="label">Paid/Allocated</span>
                                                </div>
                                            </div>
                                        </div>

                                        {(result.claim.SupervisorRemarks || result.claim.HodRemarks) && (
                                            <div className="remarks-section">
                                                <h4>Remarks</h4>
                                                {result.claim.SupervisorRemarks && (
                                                    <p><strong>Supervisor:</strong> {result.claim.SupervisorRemarks}</p>
                                                )}
                                                {result.claim.HodRemarks && (
                                                    <p><strong>HOD:</strong> {result.claim.HodRemarks}</p>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <p className="no-claim-msg">No claim record found for {month}.</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerifyClaim;
